import React, { useEffect, useRef, useState, useCallback } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ChartOptions,
  LegendItem,
  LegendElement,
  ChartEvent,
  Chart,
} from "chart.js";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import Tooltip from "../../../../componentLibrary/Tooltip";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
);

interface Data {
  _id: string;
  title: string;
  topic?: string; // Optional topic/category for multi-line labels
  count?: number; // Optional for grouped data
  total?: number; // Optional for sorting grouped data
  [key: string]: string | number | undefined; // Allow dynamic keys for grouped data
}

interface Dataset {
  label: string;
  dataKey: string;
  backgroundColor: string;
  borderColor: string;
}

interface HorizontalBarChartProps {
  data: Data[];
  title: string;
  metric?: string;
  datasets?: Dataset[]; // Optional: for multiple datasets (grouped bars)
  xAxisLabel?: string; // Optional: custom x-axis label
  maxScale?: number; // Optional: max value for x-axis scale (e.g., 5 for ratings)
  selectedDataset?: string; // Optional: name of selected dataset to highlight (dims others)
  disableSorting?: boolean; // Optional: disable automatic sorting (use data order as-is)
  onBarClick?: (itemId: string) => void; // Optional: callback when a bar is clicked
  selectedItemId?: string; // Optional: ID of the currently selected item to highlight
  tooltipText?: string; // Optional: custom tooltip text for the info icon
  maxLabelLines?: number; // Optional: max number of lines for y-axis labels (default: 2)
}

/**
 * Wraps text into multiple lines based on max characters per line.
 * Tries to break at word boundaries for better readability.
 */
function wrapText(
  text: string,
  maxCharsPerLine: number,
  maxLines: number,
): string[] {
  if (!text) return [""];

  // If text fits in one line, return as-is
  if (text.length <= maxCharsPerLine) {
    return [text];
  }

  const words = text.split(" ");
  const lines: string[] = [];
  let currentLine = "";

  for (const word of words) {
    // If word itself is longer than maxCharsPerLine, truncate it
    if (word.length > maxCharsPerLine) {
      if (currentLine) {
        lines.push(currentLine.trim());
        currentLine = "";
      }
      // If we've hit max lines, add truncated word to last line
      if (lines.length >= maxLines - 1) {
        lines.push(word.substring(0, maxCharsPerLine - 3) + "...");
        return lines.slice(0, maxLines);
      }
      lines.push(word.substring(0, maxCharsPerLine - 3) + "...");
      continue;
    }

    // Check if adding this word exceeds the line limit
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (testLine.length <= maxCharsPerLine) {
      currentLine = testLine;
    } else {
      // Current line is full, push it and start new line
      if (currentLine) {
        lines.push(currentLine.trim());
      }

      // Check if we've hit max lines
      if (lines.length >= maxLines - 1) {
        // Last line - add remaining text with truncation if needed
        const remaining = words.slice(words.indexOf(word)).join(" ");
        if (remaining.length > maxCharsPerLine) {
          lines.push(remaining.substring(0, maxCharsPerLine - 3) + "...");
        } else {
          lines.push(remaining);
        }
        return lines.slice(0, maxLines);
      }

      currentLine = word;
    }
  }

  // Push any remaining text
  if (currentLine) {
    lines.push(currentLine.trim());
  }

  return lines.slice(0, maxLines);
}

const HorizontalBarChart: React.FC<HorizontalBarChartProps> = ({
  data,
  title,
  metric = "views",
  datasets,
  xAxisLabel,
  maxScale,
  selectedDataset,
  disableSorting = false,
  onBarClick,
  selectedItemId,
  tooltipText,
  maxLabelLines = 2,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<Chart<"bar">>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  // Track which datasets are hidden via legend clicks
  const [hiddenDatasets, setHiddenDatasets] = useState<Set<string>>(new Set());

  // Sort data based on visible datasets - entries with visible data go to top
  const sortedData = React.useMemo(() => {
    if (disableSorting && hiddenDatasets.size === 0) {
      return [...data];
    }

    return [...data].sort((a, b) => {
      // When datasets are hidden via legend, sort by visibility first
      if (datasets && datasets.length > 0 && hiddenDatasets.size > 0) {
        // Calculate sum of visible dataset values for each item
        const visibleDatasets = datasets.filter(
          (ds) => !hiddenDatasets.has(ds.label),
        );

        const aVisibleSum = visibleDatasets.reduce((sum, ds) => {
          const val = a[ds.dataKey];
          return sum + (typeof val === "number" ? val : 0);
        }, 0);

        const bVisibleSum = visibleDatasets.reduce((sum, ds) => {
          const val = b[ds.dataKey];
          return sum + (typeof val === "number" ? val : 0);
        }, 0);

        // Check if items have any visible data
        const aHasVisibleData = aVisibleSum > 0;
        const bHasVisibleData = bVisibleSum > 0;

        // Items with visible data come first
        if (aHasVisibleData && !bHasVisibleData) return -1;
        if (!aHasVisibleData && bHasVisibleData) return 1;

        // If both have or both don't have visible data, sort by visible sum
        if (aVisibleSum !== bVisibleSum) {
          return bVisibleSum - aVisibleSum;
        }
      }

      // Default sorting when no legend filtering is active
      if (!disableSorting) {
        if (datasets && datasets.length > 0) {
          const aValue =
            a.total ||
            (typeof a[datasets[0].dataKey] === "number"
              ? (a[datasets[0].dataKey] as number)
              : 0) ||
            a.count ||
            0;
          const bValue =
            b.total ||
            (typeof b[datasets[0].dataKey] === "number"
              ? (b[datasets[0].dataKey] as number)
              : 0) ||
            b.count ||
            0;
          return bValue - aValue;
        }
        return (b.count || 0) - (a.count || 0);
      }

      return 0;
    });
  }, [data, datasets, disableSorting, hiddenDatasets]);

  // Handle legend click to track hidden datasets
  const handleLegendClick = useCallback(
    (_e: ChartEvent, legendItem: LegendItem, legend: LegendElement<"bar">) => {
      const datasetLabel = legendItem.text;

      // Toggle the default Chart.js visibility
      const chart = legend.chart;
      const datasetIndex = legendItem.datasetIndex;
      if (datasetIndex !== undefined) {
        const meta = chart.getDatasetMeta(datasetIndex);
        meta.hidden = !meta.hidden;
        chart.update();
      }

      // Update our hidden datasets state
      setHiddenDatasets((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(datasetLabel)) {
          newSet.delete(datasetLabel);
        } else {
          newSet.add(datasetLabel);
        }
        return newSet;
      });
    },
    [],
  );

  // Track container width for responsive font sizing
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    // Initial width
    updateWidth();

    // Listen for window resize
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  // Determine all y-axis styling based on container width
  // Strategy: Keep fonts readable, wrap labels across multiple lines on smaller screens
  let yAxisFontSize = 11;
  let yAxisTitleFontSize = 13;
  let maxCharsPerLine = 30;
  let leftPadding = 20;
  let yAxisTitlePadding = 10;

  if (containerWidth < 400) {
    // Very small screens: wrap labels across lines
    yAxisFontSize = 9;
    yAxisTitleFontSize = 11;
    maxCharsPerLine = 15;
    leftPadding = 5;
    yAxisTitlePadding = 5;
  } else if (containerWidth < 600) {
    // Small screens: more generous line width for wrapping
    yAxisFontSize = 10;
    yAxisTitleFontSize = 12;
    maxCharsPerLine = 20;
    leftPadding = 10;
    yAxisTitlePadding = 8;
  } else if (containerWidth < 800) {
    // Medium screens
    yAxisFontSize = 10;
    yAxisTitleFontSize = 12;
    maxCharsPerLine = 25;
    leftPadding = 15;
    yAxisTitlePadding = 9;
  } else if (containerWidth < 1000) {
    // Medium-large screens
    yAxisFontSize = 11;
    yAxisTitleFontSize = 13;
    maxCharsPerLine = 28;
    leftPadding = 20;
    yAxisTitlePadding = 10;
  }
  // Above 1000px uses default values

  // Build chart datasets - support both single and multiple datasets
  const chartDatasets = datasets
    ? datasets.map((dataset) => {
        const isSelected =
          !selectedDataset || dataset.label === selectedDataset;

        // Adjust opacity if a specific dataset is selected
        const backgroundColor = isSelected
          ? dataset.backgroundColor
          : dataset.backgroundColor.replace(/[\d.]+\)$/, "0.2)");

        const borderColor = isSelected
          ? dataset.borderColor
          : dataset.borderColor.replace(/rgb/, "rgba").replace(/\)$/, ", 0.3)");

        return {
          label: dataset.label,
          data: sortedData.map((item) => {
            const value = item[dataset.dataKey];
            return typeof value === "number" ? value : 0;
          }),
          backgroundColor,
          borderColor,
          borderWidth: 1,
        };
      })
    : [
        {
          label: metric.charAt(0).toUpperCase() + metric.slice(1),
          data: sortedData.map((item) => item.count || 0),
          backgroundColor: "rgba(53, 162, 235, 0.5)",
          borderColor: "rgb(53, 162, 235)",
          borderWidth: 1,
        },
      ];

  // Check if data has topics for multi-line labels
  const hasTopics = sortedData.some((item) => item.topic);

  const chartData = {
    labels: sortedData.map((item) =>
      hasTopics && item.topic
        ? [item.topic.toUpperCase(), item.title] // Visual hierarchy: UPPERCASE topic, normal case lesson
        : item.title,
    ),
    datasets: chartDatasets,
  };

  // Calculate dynamic height based on number of datasets, items, and label lines
  const numDatasets = chartDatasets.length;
  // Increase height per item based on max label lines and whether we have topics
  const baseHeightPerItem = hasTopics ? 55 : 40 + maxLabelLines * 8;
  const heightPerItem = datasets
    ? Math.max(baseHeightPerItem, 20 + numDatasets * 15)
    : baseHeightPerItem;
  const calculatedHeight = Math.max(400, sortedData.length * heightPerItem);
  const maxContainerHeight = 500;
  const containerHeight = Math.min(maxContainerHeight, calculatedHeight);
  const needsScroll = calculatedHeight > maxContainerHeight;

  const options: ChartOptions<"bar"> = {
    indexAxis: "y" as const,
    responsive: true,
    maintainAspectRatio: false,
    onClick: onBarClick
      ? (event, elements) => {
          if (elements && elements.length > 0) {
            const clickedIndex = elements[0].index;
            const clickedItem = sortedData[clickedIndex];
            if (clickedItem) {
              onBarClick(clickedItem._id);
            }
          }
        }
      : undefined,
    onHover: onBarClick
      ? (event, elements) => {
          const canvas = event.native?.target as HTMLElement;
          if (canvas) {
            canvas.style.cursor = elements.length > 0 ? "pointer" : "default";
          }
        }
      : undefined,
    scales: {
      x: {
        beginAtZero: true,
        ...(maxScale && { max: maxScale }),
        title: {
          display: true,
          text: xAxisLabel || `Number of ${metric}`,
        },
        ...(maxScale && {
          ticks: {
            stepSize: 1,
          },
        }),
      },
      y: {
        title: {
          display: true,
          text: title,
          font: {
            size: yAxisTitleFontSize,
          },
          padding: yAxisTitlePadding,
        },
        ticks: {
          autoSkip: false,
          padding: 10,
          font: function (context) {
            const item = sortedData[context.index];
            return {
              size: yAxisFontSize,
              weight:
                item && selectedItemId && item._id === selectedItemId
                  ? "bold"
                  : "normal",
            };
          },
          color: function (context) {
            const item = sortedData[context.index];
            return item && selectedItemId && item._id === selectedItemId
              ? "#EC4899"
              : "#374151";
          },
          callback: function (value, index) {
            const label = this.getLabelForValue(index);
            // Handle multi-line labels (arrays) - wrap each line if needed
            if (Array.isArray(label)) {
              const wrappedLines: string[] = [];
              for (const line of label) {
                const wrapped = wrapText(line, maxCharsPerLine, maxLabelLines);
                wrappedLines.push(...wrapped);
              }
              return wrappedLines.slice(0, maxLabelLines + 1); // Allow extra line for topic
            }
            // Wrap long labels across multiple lines
            return wrapText(label, maxCharsPerLine, maxLabelLines);
          },
        },
      },
    },
    plugins: {
      legend: {
        position: "top" as const,
        labels: {
          boxWidth: 12,
          padding: 10,
          font: {
            size: 11,
          },
        },
        onClick: datasets ? handleLegendClick : undefined,
      },
      title: {
        display: false, // We render our own title with tooltip
      },
      tooltip: {
        callbacks: {
          // Show full label in tooltip (handle multi-line labels)
          title: function (context) {
            const dataIndex = context[0].dataIndex;
            const item = sortedData[dataIndex];
            // If item has a topic, show "Topic - Title"
            if (item && item.topic) {
              return `${item.topic} - ${item.title}`;
            }
            return item?.title || context[0].label;
          },
          // Show the count specific to each category
          label: function (context) {
            const datasetLabel = context.dataset.label || "";
            const value = context.parsed.x;
            const dataIndex = context.dataIndex;
            const dataItem = sortedData[dataIndex];

            // For Overall Rating, show overall count
            if (datasetLabel === "Overall Rating") {
              const overallCount = dataItem.overallCount || dataItem.count;
              if (typeof overallCount === "number") {
                return `${datasetLabel}: ${value} (${overallCount} reviews)`;
              }
            } else {
              // For subfeedback categories, show their specific count
              const countKey = `${datasetLabel}_count`;
              const categoryCount = dataItem[countKey];
              if (typeof categoryCount === "number") {
                return `${datasetLabel}: ${value} (${categoryCount} reviews)`;
              }
            }

            return `${datasetLabel}: ${value}`;
          },
        },
      },
    },
    datasets: {
      bar: {
        // Use percentage-based thickness for grouped bars, fixed for single bars
        barPercentage: datasets ? 0.8 : 1,
        categoryPercentage: datasets ? 0.85 : 1,
        barThickness: datasets ? undefined : 25,
        maxBarThickness: datasets ? undefined : 30,
      },
    },
    layout: {
      padding: {
        right: 10,
        left: leftPadding,
        top: 10,
        bottom: 10,
      },
    },
  };

  const hasMultipleDatasets = datasets && datasets.length > 1;
  const chartTitle = datasets
    ? title
    : `${title} ${metric.charAt(0).toUpperCase() + metric.slice(1)}`;

  return (
    <div ref={containerRef} className="rounded-lg p-4 shadow-sm w-full">
      {/* Chart title with info tooltip */}
      <div className="flex justify-center items-center gap-2 mb-4">
        <h3
          className="text-base font-bold text-gray-700 m-0"
          style={{ fontFamily: "museo-slab, serif" }}
        >
          {chartTitle}
        </h3>
        {hasMultipleDatasets && (
          <Tooltip
            icon={<InformationCircleIcon className="h-5 w-5" />}
            text={tooltipText || "Click on legend items to show/hide them"}
            position="right"
          />
        )}
      </div>
      <div
        style={{
          height: `${containerHeight}px`,
          overflowY: needsScroll ? "auto" : "hidden",
          overflowX: "hidden",
        }}
      >
        <div
          style={{
            height: `${calculatedHeight}px`,
            position: "relative",
          }}
        >
          <Bar ref={chartRef} options={options} data={chartData} />
        </div>
      </div>
    </div>
  );
};

export default HorizontalBarChart;
